import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Apple, Users, TrendingUp, Edit, Loader2, Eye, Heart, 
  MessageCircle, Brain, Sparkles, FileText, CheckCircle, 
  XCircle, Clock, Mail, MapPin, Briefcase, Target, Plus, RefreshCw
} from 'lucide-react';
import { dashboardAPI, enquiriesAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import EnquiryDetailModal from './club/EnquiryDetailModal';

const NutritionistDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiTips, setAiTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(false);
  const [editStatsModal, setEditStatsModal] = useState(false);
  const [editableStats, setEditableStats] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [enquiryFilter, setEnquiryFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [enquiryDetailModal, setEnquiryDetailModal] = useState(false);
  const [receivedApplications, setReceivedApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Load data function for refresh
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [dashboardRes, applicationsRes] = await Promise.all([
        dashboardAPI.getNutritionistDashboard(),
        dashboardAPI.getReceivedApplications()
      ]);
      setDashboardData(dashboardRes.data);
      setEditableStats(dashboardRes.data.stats || {});
      setReceivedApplications(applicationsRes.data.applications || []);
    } catch (error) {
      if (!silent) {
        console.error('Failed to load dashboard:', error);
        toast.error('Failed to load dashboard data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadEnquiries = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoadingEnquiries(true);
      const statusFilter = enquiryFilter !== 'all' ? enquiryFilter : null;
      const response = await enquiriesAPI.getReceivedEnquiries(statusFilter);
      setEnquiries(response.data.enquiries || []);
    } catch (error) {
      console.error('Failed to load enquiries:', error);
    } finally {
      setLoadingEnquiries(false);
    }
  }, [enquiryFilter]);

  useEffect(() => {
    loadEnquiries();
  }, [enquiryFilter, loadEnquiries]);

  const handleMarkEnquiryRead = async (enquiryId) => {
    try {
      await enquiriesAPI.markAsRead(enquiryId);
      setEnquiries(prev => prev.map(e => e._id === enquiryId ? { ...e, read: true } : e));
    } catch (error) {
      console.error('Failed to mark enquiry read:', error);
    }
  };

  const handleEnquiryStatusUpdate = async (enquiryId, status) => {
    try {
      await enquiriesAPI.updateStatus(enquiryId, status);
      setEnquiries(prev => prev.map(e => e._id === enquiryId ? { ...e, status } : e));
      setSelectedEnquiry(prev => prev ? { ...prev, status } : prev);
      toast.success(`Enquiry marked as ${status}`);
    } catch (error) {
      toast.error('Failed to update enquiry status');
    }
  };

  useEffect(() => {
    loadData();
    // Auto-refresh every 15 seconds
    const refreshInterval = setInterval(() => loadData(true), 15000);
    return () => clearInterval(refreshInterval);
  }, [loadData]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getNutritionistDashboard();
      setDashboardData(response.data);
      setEditableStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadAITips = async () => {
    try {
      setLoadingTips(true);
      const response = await dashboardAPI.getAITips();
      setAiTips(response.data.tips || []);
    } catch (error) {
      console.error('Failed to load AI tips:', error);
    } finally {
      setLoadingTips(false);
    }
  };

  const loadReceivedApplications = async () => {
    try {
      setLoadingApplications(true);
      const response = await dashboardAPI.getReceivedApplications();
      setReceivedApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleSaveStats = async () => {
    try {
      await dashboardAPI.updateStats(editableStats);
      toast.success('Stats updated successfully!');
      setEditStatsModal(false);
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update stats');
    }
  };

  const updateStat = (field, value) => {
    setEditableStats({ ...editableStats, [field]: value });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'accepted': 'badge-success',
      'rejected': 'badge-danger',
      'applied': 'badge-primary',
      'pending': 'badge-warning'
    };
    return statusMap[status?.toLowerCase()] || 'badge-neutral';
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const connections = dashboardData?.connections || {};
  const contentAnalytics = dashboardData?.contentAnalytics || {};
  const clientProspects = dashboardData?.clientProspects || [];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="nutritionist-dashboard">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Nutritionist Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your clients and meal plans
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button onClick={() => navigate('/jobs')} className="btn-primary" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Post Job
            </Button>
            <Button onClick={() => navigate('/discover')} variant="outline" size="sm">
              <Users className="w-4 h-4 mr-2" />
              Find Athletes
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Profile Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <Card className="card-premium overflow-hidden border-0">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex-shrink-0">
                <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
                <AvatarFallback className="text-lg font-bold bg-primary text-primary-foreground">
                  {currentUser.name?.[0] || 'N'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-foreground truncate">{currentUser.name}</h2>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <Apple className="w-3 h-3 mr-1.5 text-purple-400" />
                        Nutritionist
                      </span>
                      <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                        <MapPin className="w-3 h-3 mr-1.5" />
                        {currentUser.location || 'Not specified'}
                      </span>
                      <span className="inline-flex items-center text-xs font-semibold text-foreground bg-primary/10 px-2.5 py-1 rounded-full">
                        <Users className="w-3 h-3 mr-1.5 text-primary" />
                        {connections.followers || 0} followers
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditStatsModal(true)}
                    className="text-xs h-8 px-3"
                    data-testid="edit-stats-btn"
                  >
                    <Edit className="w-3 h-3 mr-1.5" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-muted/50">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="applications">
            Received Applications
            {receivedApplications.length > 0 && (
              <Badge className="ml-2 badge-primary">{receivedApplications.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="enquiries">
            Enquiries
            {enquiries.filter(e => e.status === 'pending').length > 0 && (
              <Badge className="ml-2 badge-primary">{enquiries.filter(e => e.status === 'pending').length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          {/* Stats Grid */}
          <motion.div 
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          >
            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Active Clients</p>
                    <h3 className="stat-value mt-1">{stats.activeClients || 0}</h3>
                  </div>
                  <div className="icon-container-blue">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Experience</p>
                    <h3 className="stat-value mt-1">{stats.yearsExperience || 0}y</h3>
                  </div>
                  <div className="icon-container-purple">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Followers</p>
                    <h3 className="stat-value mt-1">{connections.followers || 0}</h3>
                  </div>
                  <div className="icon-container-pink">
                    <Heart className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={cardVariants}>
              <div className="stat-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="stat-label">Meal Plans</p>
                    <h3 className="stat-value mt-1">{stats.mealPlansCreated || 0}</h3>
                  </div>
                  <div className="icon-container-green">
                    <Apple className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            {/* Content Analytics */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Eye className="w-4 h-4 text-primary" />
                    Content Performance
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Posts</span>
                      <span className="text-sm font-semibold">{contentAnalytics.postsCount || 0}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Total Likes</span>
                      <span className="text-sm font-semibold flex items-center">
                        <Heart className="w-3.5 h-3.5 mr-1.5 text-primary" />
                        {contentAnalytics.totalLikes || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Comments</span>
                      <span className="text-sm font-semibold flex items-center">
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-purple-400" />
                        {contentAnalytics.totalComments || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm text-muted-foreground">Profile Views</span>
                      <span className="text-sm font-semibold flex items-center">
                        <Eye className="w-3.5 h-3.5 mr-1.5 text-purple-300" />
                        {contentAnalytics.profileViews || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* AI Tips */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center justify-between text-base font-semibold">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-primary" />
                      <Sparkles className="w-3 h-3 text-purple-300" />
                      AI Tips
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={loadAITips}
                      disabled={loadingTips}
                      className="h-8 w-8 p-0"
                    >
                      {loadingTips ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  {loadingTips ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-12 bg-muted/30 animate-pulse rounded-xl" />
                      ))}
                    </div>
                  ) : aiTips.length > 0 ? (
                    <ul className="space-y-3">
                      {aiTips.slice(0, 3).map((tip, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs text-white mr-2 flex-shrink-0 mt-0.5 bg-primary">
                            {index + 1}
                          </span>
                          <span className="text-muted-foreground">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-4">
                      <Sparkles className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">Click refresh for tips</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Potential Clients */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="card-premium h-full">
                <CardHeader className="pb-3 pt-4 px-5">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Target className="w-4 h-4 text-primary" />
                    Potential Clients
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  {clientProspects.length > 0 ? (
                    <div className="space-y-3">
                      {clientProspects.slice(0, 4).map((prospect, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 cursor-pointer transition-colors hover-lift"
                          onClick={() => navigate(`/profile/${prospect.username}`)}
                        >
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={prospect.profileImage} alt={prospect.name} />
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">{prospect.name?.[0] || 'A'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate text-foreground">{prospect.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {prospect.sport} • {prospect.location}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No prospects found</p>
                    </div>
                  )}
                  <Button 
                    className="w-full mt-4 btn-primary" 
                    onClick={() => navigate('/discover')}
                  >
                    Discover More
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="card-premium">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Target className="w-4 h-4 text-primary" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Button className="btn-primary justify-start" onClick={() => navigate('/jobs')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Post Job Opening
                  </Button>
                  <Button variant="outline" className="justify-start hover-lift" onClick={() => navigate('/discover')}>
                    <Users className="w-4 h-4 mr-2" />
                    Find Athletes
                  </Button>
                  <Button variant="outline" className="justify-start hover-lift" onClick={() => navigate('/messages')}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Messages
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="applications">
          <Card className="card-premium">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="icon-container-orange">
                    <FileText className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-lg font-bold">Applications Received</span>
                </div>
                <Badge className="badge-neutral">{receivedApplications.length} Total</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingApplications ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 bg-muted/30 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : receivedApplications.length > 0 ? (
                <div className="space-y-3">
                  {receivedApplications.map((app) => (
                    <div 
                      key={app._id}
                      className="p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all border border-transparent hover:border-primary/20"
                      data-testid={`application-${app._id}`}
                    >
                      <div className="flex items-start gap-4">
                        <Avatar className="w-12 h-12 ring-2 ring-primary/20">
                          <AvatarImage src={app.applicant?.profileImage} alt={app.applicant?.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">{app.applicant?.name?.[0] || 'A'}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold text-foreground">{app.applicant?.name || 'Unknown'}</h4>
                              <p className="text-sm text-muted-foreground">Applied for: <span className="font-medium text-foreground">{app.jobTitle}</span></p>
                              {app.applicant?.location && (
                                <div className="flex items-center text-xs text-muted-foreground mt-1">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {app.applicant.location}
                                </div>
                              )}
                            </div>
                            <span className={getStatusBadgeClass(app.status)}>
                              {app.status || 'Applied'}
                            </span>
                          </div>
                          {app.coverLetter && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {app.coverLetter}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/profile/${app.applicant?.username || app.applicant?._id}`)}>
                              <Eye className="w-3 h-3 mr-1" /> View Profile
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/messages?userId=${app.applicant?._id}`)}>
                              <Mail className="w-3 h-3 mr-1" /> Message
                            </Button>
                          </div>
                          {app.appliedAt && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              Applied: {new Date(app.appliedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">No applications received yet</p>
                  <Button onClick={() => navigate('/jobs')} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" /> Post a Job
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enquiries Tab */}
        <TabsContent value="enquiries">
          <Card className="card-premium border-0">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="icon-container-pink">
                    <Mail className="w-5 h-5 text-purple-300" />
                  </div>
                  <span className="text-lg font-bold">Received Enquiries</span>
                </div>
                <select
                  value={enquiryFilter}
                  onChange={(e) => setEnquiryFilter(e.target.value)}
                  className="text-xs bg-muted/50 border border-border rounded-lg px-3 py-1.5"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="responded">Responded</option>
                </select>
              </div>

              {loadingEnquiries ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                </div>
              ) : enquiries.length > 0 ? (
                <div className="space-y-3">
                  {enquiries.map((enquiry) => (
                    <div
                      key={enquiry._id}
                      onClick={() => { setSelectedEnquiry(enquiry); setEnquiryDetailModal(true); handleMarkEnquiryRead(enquiry._id); }}
                      className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-primary/5 border ${
                        !enquiry.read ? 'border-primary/20 bg-primary/5' : 'border-border'
                      }`}
                    >
                      <Avatar className="w-12 h-12 ring-2 ring-purple-300/20">
                        <AvatarImage src={enquiry.enquirer?.profileImage} alt={enquiry.enquirer?.name} />
                        <AvatarFallback className="bg-purple-300/10 text-purple-400 font-semibold">
                          {enquiry.enquirer?.name?.[0] || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="font-semibold text-sm truncate">{enquiry.enquirer?.name || 'Unknown'}</p>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            enquiry.status === 'pending' ? 'bg-purple-400/20 text-purple-400'
                              : enquiry.status === 'responded' ? 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}>{enquiry.status}</span>
                        </div>
                        <p className="text-xs text-foreground truncate">{enquiry.subject || 'No subject'}</p>
                        <p className="text-[11px] text-muted-foreground truncate mt-0.5">{enquiry.message?.substring(0, 80)}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(enquiry.createdAt).toLocaleDateString()}</p>
                      </div>
                      {!enquiry.read && <div className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground font-medium">No enquiries yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Enquiries from your profile visitors will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Enquiry Detail Modal */}
      {selectedEnquiry && (
        <EnquiryDetailModal
          isOpen={enquiryDetailModal}
          onClose={() => setEnquiryDetailModal(false)}
          enquiry={selectedEnquiry}
          onStatusUpdate={handleEnquiryStatusUpdate}
          onNavigate={navigate}
        />
      )}

      {/* Edit Stats Modal */}
      <Dialog open={editStatsModal} onOpenChange={setEditStatsModal}>
        <DialogContent className="max-w-md p-0" data-testid="edit-stats-modal">
          <div className="bg-gradient-to-r from-purple-500/10 to-primary/10 p-6 rounded-t-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-3">
                <div className="icon-container-green">
                  <Edit className="w-5 h-5 text-purple-500" />
                </div>
                Edit Professional Stats
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold">Active Clients</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.activeClients || 0}
                onChange={(e) => updateStat('activeClients', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Years of Experience</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.yearsExperience || 0}
                onChange={(e) => updateStat('yearsExperience', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Meal Plans Created</label>
              <Input
                type="number"
                className="input-premium"
                value={editableStats.mealPlansCreated || 0}
                onChange={(e) => updateStat('mealPlansCreated', parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Certifications</label>
              <Input
                type="text"
                className="input-premium"
                value={
                  Array.isArray(editableStats.certifications)
                    ? editableStats.certifications.map(c => typeof c === 'object' ? c.name : c).join(', ')
                    : editableStats.certifications || ''
                }
                onChange={(e) => updateStat('certifications', e.target.value)}
                placeholder="e.g., RD, CSSD, LD"
              />
              <p className="text-xs text-muted-foreground">Comma separated</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold">Specializations</label>
              <Input
                type="text"
                className="input-premium"
                value={
                  Array.isArray(editableStats.specializations)
                    ? editableStats.specializations.join(', ')
                    : editableStats.specializations || ''
                }
                onChange={(e) => updateStat('specializations', e.target.value)}
                placeholder="e.g., Sports Nutrition, Weight Management"
              />
              <p className="text-xs text-muted-foreground">Comma separated</p>
            </div>
          </div>
          <DialogFooter className="p-4 border-t border-border bg-muted/30 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditStatsModal(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button className="btn-primary w-full sm:w-auto" onClick={handleSaveStats}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NutritionistDashboard;
